import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from '@marketplace/ui';
import { type ProviderSubscription } from '@marketplace/contracts';
import { BillingButton } from './BillingButton.js';
import { gateStateOf, isSubscriptionRefusal, type CheckoutFrom } from './billing.js';

/** The feed pages start Checkout from a quote or from a bid; the other entry points are pages. */
type GateFrom = Extract<CheckoutFrom, 'quote' | 'bid'>;

interface GateProps {
  /** `null` when it could not be read: nothing is shown, and the API stays the judge. */
  subscription: ProviderSubscription | null;
  locale: string;
  from: GateFrom;
}

/**
 * `W5-T13` §4.6 — on `/feed` and `/feed/:id`, while the provider is not entitled to quote or bid.
 *
 * It says which of §3's two states they are in — never subscribed, or lapsed, with what still stands
 * — and **Subscribe** starts Checkout from where they are. It is the earlier of the two pending steps
 * (§4.1's order), so while it shows the page does not show `PayoutsPendingBanner`: the route decides,
 * through `isSubscriptionPending`.
 */
export function SubscriptionRequiredBanner({
  subscription,
  locale,
  from,
}: GateProps): ReactElement | null {
  const { t } = useTranslation();
  const state = gateStateOf(subscription);
  if (state === null) return null;

  return (
    <section
      className="mp-section"
      aria-labelledby="subscription-banner-title"
      data-testid="subscription-banner"
    >
      <h2 id="subscription-banner-title">{t(`billing.gate.${state}.title`)}</h2>
      <p>{t(`billing.gate.${state}.body`)}</p>
      <BillingButton
        locale={locale}
        intent="subscribe"
        from={from}
        label={t('billing.action.subscribe')}
      />
    </section>
  );
}

/**
 * `W5-T13` §4.6 — the same message in its short form, under the quote or bid button the provider
 * cannot use yet. The form above stays visible, so they see what they will be able to do
 * (`PayoutsCtaLine`'s pattern). Rendered after the form, never in it: **Subscribe** is a form too.
 */
export function SubscriptionCtaLine({
  subscription,
  locale,
  from,
}: GateProps): ReactElement | null {
  const { t } = useTranslation();
  if (gateStateOf(subscription) === null) return null;

  return (
    <div className="mp-meta" data-testid="subscription-cta">
      <p>{t('billing.gate.cta_line')}</p>
      <BillingButton
        locale={locale}
        intent="subscribe"
        from={from}
        label={t('billing.action.subscribe')}
        variant="secondary"
      />
    </div>
  );
}

/**
 * `W5-T13` §7 — a `409 SUBSCRIPTION_REQUIRED` answer to the quote or bid form, from a page that was
 * stale about it: the refusal and **Subscribe**, never the form's generic error. `failure` is the
 * form's failure key; anything else is the form's own to show.
 */
export function SubscriptionRefused({
  failure,
  locale,
  from,
}: {
  failure: string | null;
  locale: string;
  from: GateFrom;
}): ReactElement | null {
  const { t } = useTranslation();
  if (!isSubscriptionRefusal(failure)) return null;

  return (
    <div data-testid="subscription-refused">
      <Alert tone="error">{t(failure)}</Alert>
      <BillingButton
        locale={locale}
        intent="subscribe"
        from={from}
        label={t('billing.action.subscribe')}
      />
    </div>
  );
}

/**
 * Whether the subscription is the step still pending, so the payouts banner and line wait for it:
 * one pending step at a time, subscription first (§4.1).
 */
export function isSubscriptionPending(subscription: ProviderSubscription | null): boolean {
  return gateStateOf(subscription) !== null;
}
